import { useEffect, useRef } from "react";

/**
 * useDebounce - Debounces function calls
 * Prevents rapid repeated calls by waiting for a quiet period
 * 
 * @param {Function} callback - Function to debounce
 * @param {number} delay - Delay in milliseconds before calling
 * @param {Array} deps - Dependency array
 * @returns {void}
 * 
 * @example
 * useDebounce(() => saveToStorage(state), 1000, [state])
 */
export function useDebounce(callback, delay = 1000, deps = []) {
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      callback();
    }, delay);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, deps);
}

/**
 * useThrottle - Throttles function calls
 * Limits function execution to once per specified interval
 * 
 * @param {Function} callback - Function to throttle
 * @param {number} delay - Minimum time between calls in milliseconds
 * @param {Array} deps - Dependency array
 * @returns {void}
 */
export function useThrottle(callback, delay = 1000, deps = []) {
  const lastRunRef = useRef(Date.now());

  useEffect(() => {
    const now = Date.now();
    const timeSinceLastRun = now - lastRunRef.current;

    if (timeSinceLastRun >= delay) {
      callback();
      lastRunRef.current = now;
    } else {
      const timeoutId = setTimeout(() => {
        callback();
        lastRunRef.current = Date.now();
      }, delay - timeSinceLastRun);

      return () => clearTimeout(timeoutId);
    }
  }, deps);
}
